import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertTriangle, Play } from 'lucide-react';
import documentRenewalBatchService from '../../../services/documentRenewalBatchService';

const WorkflowProgressPanel = ({ batchId, workflowExecutions, onRefresh }) => {
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorkflowSteps();
  }, []);

  const loadWorkflowSteps = async () => {
    try {
      setLoading(true);
      const steps = await documentRenewalBatchService?.getWorkflowSteps();
      setWorkflowSteps(steps || []);
    } catch (err) {
      console.error('Error loading workflow steps:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStepExecution = (stepOrder) => {
    return workflowExecutions?.find(exec => exec?.stepOrder === stepOrder);
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'in_progress':
        return <Play className="h-6 w-6 text-blue-600 animate-pulse" />;
      case 'failed':
        return <AlertTriangle className="h-6 w-6 text-red-600" />;
      default:
        return <Clock className="h-6 w-6 text-gray-400" />;
    }
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-50';
      case 'in_progress':
        return 'border-blue-500 bg-blue-50';
      case 'failed':
        return 'border-red-500 bg-red-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Workflow Progress</h3>
      </div>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* Steps */}
        <div className="space-y-6">
          {workflowSteps?.map((step, index) => {
            const execution = getStepExecution(step?.stepOrder);
            const status = execution?.stepStatus || 'pending';

            return (
              <div key={step?.id} className="relative flex items-start">
                {/* Icon */}
                <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 bg-white">
                  {getStepIcon(status)}
                </div>

                {/* Content */}
                <div className={`ml-6 flex-1 p-4 rounded-lg border-2 ${getStepColor(status)}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">
                        Step {step?.stepOrder}: {step?.stepName}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">{step?.stepDescription}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        status === 'completed' ? 'bg-green-100 text-green-800' :
                        status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        status === 'failed'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {status?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Execution details */}
                  {execution && (
                    <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                      {execution?.itemsProcessed > 0 && (
                        <div>
                          <p className="text-gray-600">Processed</p>
                          <p className="font-semibold text-gray-900">{execution?.itemsProcessed}</p>
                        </div>
                      )}
                      {execution?.itemsSucceeded > 0 && (
                        <div>
                          <p className="text-gray-600">Succeeded</p>
                          <p className="font-semibold text-green-600">{execution?.itemsSucceeded}</p>
                        </div>
                      )}
                      {execution?.itemsFailed > 0 && (
                        <div>
                          <p className="text-gray-600">Failed</p>
                          <p className="font-semibold text-red-600">{execution?.itemsFailed}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error message */}
                  {execution?.errorMessage && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{execution?.errorMessage}</p>
                    </div>
                  )}

                  {/* Step metadata */}
                  <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                    {step?.isAutomated && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Automated</span>
                    )}
                    {step?.requiresApproval && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">Requires Approval</span>
                    )}
                    {step?.estimatedDurationMinutes && (
                      <span>Est. {step?.estimatedDurationMinutes} min</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkflowProgressPanel;